import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ConfirmProvider } from './context/ConfirmContext';
import { SystemConfigProvider, useSystemConfig } from './context/SystemConfigContext';
import {
  I18N_LOADED_EVENT,
  PREFERRED_LANGUAGE_CHANGED_EVENT,
  SHOP_CONTENT_LANGUAGE_EVENT,
  detectInitialLanguage,
  persistPreferredLanguage,
  readHolylandContentLanguage,
  readLanguageFromSearch,
  _t,
  type ShopContentLanguage,
} from './lib/i18n';
import { api } from './lib/api';
import { applySystemTheme } from './lib/systemThemeCss';
import { clearBootSplashPaint } from './lib/bootSplash';
import { normalizeTenantTheme } from './lib/tenantTheme';
import { applyTenantThemeCss, clearTenantThemeCss } from './lib/tenantThemeCss';
import { tenantSlugFromHostname } from './lib/tenantHost';
import { brand } from './lib/brand';
import { isAppVitrinePath, isWebsiteStorefront, toWebsiteLocation } from './lib/storefrontFace';
import { STOREFRONT_UPDATED_EVENT } from './lib/storefrontEvents';
import { VISITING_CARD_UPDATED_EVENT } from './lib/visitingCardEvents';
import { connectStorefrontSocket, disconnectStorefrontSocket } from './lib/storefrontSocket';
import type { Language, TenantContext } from './types';
import VisitingCardPublicPage from './pages/VisitingCardPublicPage';
import PublicShowcaseClosedPage from './pages/PublicShowcaseClosedPage';
import { CookieConsentBanner } from './components/legal/CookieConsentBanner';
import { FloatingFabDockProvider } from './components/common/FloatingFabDock';

/**
 * Everything below the public entry points is split out: a visitor opening a storefront or a
 * visiting card must not download the admin cabinet. Views from one module share one chunk.
 */
const lazyPlatform = <T extends Record<string, unknown>>(
  load: () => Promise<T>,
  key: keyof T,
) => lazy(() => load().then((m) => ({ default: m[key] as never })));

const AppLayout = lazy(() => import('./components/app/AppLayout'));
const AppIndexRedirect = lazyPlatform(
  () => import('./components/app/AppLayout'),
  'AppIndexRedirect',
);
const Landing = lazy(() => import('./pages/Landing'));
// The apex Alive Album landing skips the shared storefront module and the cabinet code it pulls in.
const AliveInfoPage = lazy(() => import('./components/landing/AliveInfoPage')
  .then((module) => ({ default: module.AliveInfoPage })));
const AliveLandingV2 = lazy(() => import('./components/landing/AliveLandingV2')
  .then((module) => ({ default: module.AliveLandingV2 })));
const Login = lazy(() => import('./pages/Login'));
const DigitalAlbumPage = lazy(() => import('./pages/DigitalAlbumPage'));
const PublicGalleryPage = lazy(() => import('./pages/PublicGalleryPage'));
const PublicGlobalBookPage = lazy(() => import('./pages/PublicGlobalBookPage'));
const AliveAlbumBotPage = lazy(() => import('./pages/telegram/AliveAlbumBotPage').then((m) => ({
  default: m.AliveAlbumBotPage,
})));
const PublicReviewsPage = lazy(() => import('./pages/PublicReviewsPage'));
const PublicWorkPage = lazy(() => import('./pages/PublicWorkPage'));
const AnnouncementsMobilePage = lazy(() => import('./pages/AnnouncementsMobilePage'));
const LegalPage = lazyPlatform(() => import('./components/legal/LegalPage'), 'LegalPage');
const PlatformAdminLayout = lazy(() => import('./components/platform/PlatformAdminLayout'));
const PlatformSuperAdminRoute = lazy(() => import('./components/platform/PlatformSuperAdminRoute'));
const PlatformOverviewPage = lazy(() => import('./pages/platform/PlatformOverviewPage'));
const PlatformTenantsPage = lazy(() => import('./pages/platform/PlatformTenantsPage'));
const PlatformMaintenancePage = lazy(() => import('./pages/platform/PlatformMaintenancePage'));
const PlatformJoinRequestsPage = lazy(() => import('./pages/platform/PlatformJoinRequestsPage'));
const PlatformReferralApplicationsPage = lazy(
  () => import('./pages/platform/PlatformReferralApplicationsPage'),
);
const PlatformReferralPartnersPage = lazy(
  () => import('./pages/platform/PlatformReferralPartnersPage'),
);
const PlatformReferralConversionsPage = lazy(
  () => import('./pages/platform/PlatformReferralConversionsPage'),
);
const PlatformReferralPayoutsPage = lazy(
  () => import('./pages/platform/PlatformReferralPayoutsPage'),
);
const PlatformReferralSettingsPage = lazy(
  () => import('./pages/platform/PlatformReferralSettingsPage'),
);
const PlatformExtensionOrdersPage = lazy(
  () => import('./pages/platform/PlatformExtensionOrdersPage'),
);
const PlatformExtensionsPage = lazy(() => import('./pages/platform/PlatformExtensionsPage'));
const PlatformRoomMockupPackPage = lazy(
  () => import('./pages/platform/PlatformRoomMockupPackPage'),
);
const PlatformUiPresetsPage = lazy(() => import('./pages/platform/PlatformUiPresetsPage'));
const PlatformStaffPage = lazy(() => import('./pages/platform/PlatformStaffPage'));
const PlatformProfilePage = lazy(() => import('./pages/platform/PlatformProfilePage'));
const PlatformSeoPage = lazy(() => import('./pages/platform/PlatformSeoPage'));
const PlatformPlanCatalogPage = lazy(() => import('./pages/platform/PlatformPlanCatalogPage'));
const PlatformGiftCouponsPage = lazy(() => import('./pages/platform/PlatformGiftCouponsPage'));
const PlatformManualPaymentsPage = lazy(() => import('./pages/platform/PlatformManualPaymentsPage'));
const PartnerCabinetPage = lazy(() => import('./pages/partner/PartnerCabinetPage'));
const PartnerLoginPage = lazyPlatform(
  () => import('./pages/partner/PartnerCabinetPage'),
  'PartnerLoginPage',
);
const TelegramMiniAppShell = lazy(() => import('./pages/telegram/TelegramMiniAppShell'));

const appView = (key: keyof typeof import('./pages/app/AppViews')) =>
  lazyPlatform(() => import('./pages/app/AppViews'), key);

const AnnouncementsView = appView('AnnouncementsView');
const CartView = appView('CartView');
const CatalogView = appView('CatalogView');
const CategoriesView = appView('CategoriesView');
const CrmView = appView('CrmView');
const AttributesView = appView('AttributesView');
const OptionSetsView = appView('OptionSetsView');
const VariantTemplatesView = appView('VariantTemplatesView');
const AddonTemplatesView = appView('AddonTemplatesView');
const FavoritesView = appView('FavoritesView');
const ImportView = appView('ImportView');
const ImportMediaView = appView('ImportMediaView');
const ExportView = appView('ExportView');
const AlbumsView = appView('AlbumsView');
const VisitingCardView = appView('VisitingCardView');
const NewsView = appView('NewsView');
const NotesView = appView('NotesView');
const GalleriesView = appView('GalleriesView');
const GallerySettingsView = appView('GallerySettingsView');
const AlbumReviewsView = appView('AlbumReviewsView');
const AlbumPublishedReviewsView = appView('AlbumPublishedReviewsView');
const AlbumRequestsView = appView('AlbumRequestsView');
const BooksView = appView('BooksView');
const BookEditorView = appView('BookEditorView');
const PhotographerPlanView = appView('PhotographerPlanView');
const TrashView = appView('TrashView');
const OrdersView = appView('OrdersView');
const OverviewView = appView('OverviewView');
const MarketplaceView = appView('MarketplaceView');
const PublisherView = appView('PublisherView');
const PricesView = appView('PricesView');
const PromotionsView = appView('PromotionsView');
const ProductsView = appView('ProductsView');
const ProfileView = appView('ProfileView');
const AboutView = appView('AboutView');
const SettingsView = appView('SettingsView');
const StaffView = appView('StaffView');
const StockAlertsView = appView('StockAlertsView');
const StockView = appView('StockView');
const SubcategoriesView = appView('SubcategoriesView');
const TenantModuleGuard = appView('TenantModuleGuard');
import {
  isBrandAdminModuleEnabled,
  isBrandStorefrontPathEnabled,
} from './lib/brandRoutePolicy';
import { blogPath, parseBlogPath } from './lib/storefrontBlog.js';

function useTenantContext() {
  const location = useLocation();
  const explicitTenant = new URLSearchParams(location.search).get('tenant');
  const hostnameSlug = tenantSlugFromHostname();
  const isPlatformRoute = location.pathname.startsWith('/platform');
  const skipTenantFetch =
    isPlatformRoute ||
    location.pathname === '/login' ||
    (location.pathname === '/' && !explicitTenant && !hostnameSlug);
  const missingTenantOnApp =
    location.pathname.startsWith('/app') && !explicitTenant && !hostnameSlug;
  const tenantSlugForFetch = explicitTenant || hostnameSlug;

  const [tenant, setTenant] = useState<TenantContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedOnce = useRef(false);

  const load = useCallback(async () => {
    const initial = !hasLoadedOnce.current;
    if (initial) {
      setLoading(true);
      setError(null);
    }
    if (skipTenantFetch) {
      setTenant(null);
      setError(null);
      hasLoadedOnce.current = true;
      if (initial) {
        setLoading(false);
      }
      return;
    }
    if (missingTenantOnApp) {
      setTenant(null);
      setError(null);
      hasLoadedOnce.current = true;
      if (initial) {
        setLoading(false);
      }
      return;
    }
    try {
      const data = await api.fetchTenantContext(tenantSlugForFetch);
      setTenant(data);
      hasLoadedOnce.current = true;
    } catch (err) {
      setTenant(null);
      setError(err instanceof Error ? err.message : 'tenant_failed');
    } finally {
      if (initial) {
        setLoading(false);
      }
    }
  }, [skipTenantFetch, missingTenantOnApp, tenantSlugForFetch]);

  useEffect(() => {
    hasLoadedOnce.current = false;
  }, [tenantSlugForFetch, skipTenantFetch, missingTenantOnApp]);

  // Refetch on navigation (e.g. settings → landing) without full-page loading flash.
  useEffect(() => {
    void load();
  }, [load, location.pathname, location.search]);

  // Socket push: tenant storefront settings changed — refresh policies/theme.
  useEffect(() => {
    const onStorefrontUpdated = () => {
      void load();
    };
    window.addEventListener(STOREFRONT_UPDATED_EVENT, onStorefrontUpdated);
    window.addEventListener(VISITING_CARD_UPDATED_EVENT, onStorefrontUpdated);
    return () => {
      window.removeEventListener(STOREFRONT_UPDATED_EVENT, onStorefrontUpdated);
      window.removeEventListener(VISITING_CARD_UPDATED_EVENT, onStorefrontUpdated);
    };
  }, [load]);

  return { tenant, loading, error, missingTenantOnApp };
}

function MissingPage({ lang }: { lang: Language }) {
  return (
    <main
      className="mx-auto flex min-h-screen max-w-lg flex-col justify-center gap-4 px-6"
      dir={lang === 'he' ? 'rtl' : 'ltr'}
    >
      <h1 className="text-2xl font-semibold text-[var(--text-primary)]">{_t('common.pageMissing', lang)}</h1>
      <Link to="/" className="underline">{_t('common.pageMissingHome', lang)}</Link>
    </main>
  );
}

function PublicGalleryRoute({
  lang,
  setLang,
  landingI18n,
  logoUrl,
  book = false,
  bookCode = '',
  showLink = false,
}: {
  lang: Language;
  setLang: (lang: Language) => void;
  landingI18n?: unknown;
  logoUrl?: string | null;
  book?: boolean;
  bookCode?: string;
  showLink?: boolean;
}) {
  const { galleryId } = useParams();
  if (!galleryId) return null;
  const extraBook = /^d[1-8]$/.test(bookCode) ? bookCode : '';
  return (
    <PublicGalleryPage
      lang={lang}
      setLang={setLang}
      landingI18n={landingI18n}
      logoUrl={logoUrl}
      galleryId={galleryId}
      book={book || Boolean(extraBook)}
      bookCode={extraBook}
      showLink={showLink}
    />
  );
}

function PublicShowRoute({
  lang,
  setLang,
  landingI18n,
  logoUrl,
}: {
  lang: Language;
  setLang: (lang: Language) => void;
  landingI18n?: unknown;
  logoUrl?: string | null;
}) {
  return (
    <PublicGalleryRoute
      lang={lang}
      setLang={setLang}
      landingI18n={landingI18n}
      logoUrl={logoUrl}
      showLink
    />
  );
}

function isAliveAlbumShowcaseClosed(tenant: TenantContext | null): boolean {
  return brand.id === 'alivealbum' && Boolean(tenant) && tenant?.publicShowcase !== true;
}

function StorefrontFaceGate({
  tenant,
  lang,
  children,
}: {
  tenant: TenantContext | null;
  lang: Language;
  children: React.ReactNode;
}) {
  const location = useLocation();
  const { isTenantStaff } = useAuth();
  if (isAliveAlbumShowcaseClosed(tenant)) {
    const path = location.pathname.replace(/\/+$/, '') || '/';
    const guestAppHome = path === '/app' && !isTenantStaff;
    if (guestAppHome || isAppVitrinePath(location.pathname, isTenantStaff)) {
      return <PublicShowcaseClosedPage lang={lang} />;
    }
  }
  if (isWebsiteStorefront(tenant) && isAppVitrinePath(location.pathname, isTenantStaff)) {
    const dest = toWebsiteLocation(location.search, tenant?.slug, location.pathname);
    return (
      <Navigate
        to={{ pathname: dest.pathname, search: dest.search }}
        state={dest.state}
        replace
      />
    );
  }
  return children;
}

function AppRoutes() {
  const location = useLocation();
  const navigate = useNavigate();
  const [lang, setLangState] = useState<Language>(() => detectInitialLanguage());
  const { tenant, loading, error, missingTenantOnApp } = useTenantContext();
  const { session, isClient, isTenantStaff } = useAuth();
  const { config } = useSystemConfig();
  const isPlatformRoute = location.pathname.startsWith('/platform');

  useEffect(() => {
    if (!tenant?.id || isPlatformRoute) {
      disconnectStorefrontSocket();
      return undefined;
    }
    connectStorefrontSocket(tenant.id, {
      clientId: isClient ? session?.id : null,
      joinAdmin: isTenantStaff,
    });
    return () => disconnectStorefrontSocket();
  }, [tenant?.id, isPlatformRoute, isClient, isTenantStaff, session?.id]);

  const setLang = (next: Language) => {
    setLangState(next);
    persistPreferredLanguage(next);
    const catalogHost = (brand.id === 'catalogofworks' || brand.id === 'sitonaim')
      && Boolean(tenantSlugFromHostname());
    if (!catalogHost) return;
    const path = location.pathname;
    const work = /^(?:\/(?:ru|en|he))?\/w\/([0-9a-f-]{36})\/?$/i.exec(path);
    const blog = parseBlogPath(path);
    const target = work
      ? `/${next}/w/${work[1]}${location.search}${location.hash}`
      : blog
        ? `${blogPath(next, blog.key)}${location.search}${location.hash}`
      : (path === '/' || /^\/(ru|en|he)\/?$/.test(path))
        ? `/${next}${location.search}${location.hash}`
        : '';
    if (target && target !== `${path}${location.search}${location.hash}`) navigate(target);
  };

  const extraShopLanguages = tenant?.enabledExtensions?.includes('storefront_languages') === true;
  const publicShopChrome = !location.pathname.startsWith('/app');
  const [shopChromeLang, setShopChromeLang] = useState<ShopContentLanguage>(lang);
  useEffect(() => {
    if (!extraShopLanguages || !publicShopChrome) {
      setShopChromeLang(lang);
      return undefined;
    }
    const sync = () => setShopChromeLang(readHolylandContentLanguage(true, lang));
    sync();
    window.addEventListener(SHOP_CONTENT_LANGUAGE_EVENT, sync);
    return () => window.removeEventListener(SHOP_CONTENT_LANGUAGE_EVENT, sync);
  }, [extraShopLanguages, publicShopChrome, lang]);

  // Visiting card (/c) keeps its own lang state; sync App chrome (cookie banner, …) when it persists.
  useEffect(() => {
    const onPreferred = (event: Event) => {
      const next = (event as CustomEvent<{ lang?: Language }>).detail?.lang;
      if (!next || !['ru', 'en', 'he'].includes(next)) return;
      setLangState((prev) => (prev === next ? prev : next));
    };
    window.addEventListener(PREFERRED_LANGUAGE_CHANGED_EVENT, onPreferred);
    return () => window.removeEventListener(PREFERRED_LANGUAGE_CHANGED_EVENT, onPreferred);
  }, []);

  // Platform → storefront handoff: honor ?lang= even after first paint / SPA nav.
  useEffect(() => {
    const fromUrl = readLanguageFromSearch(location.search);
    if (fromUrl && fromUrl !== lang) {
      setLang(fromUrl);
    }
  }, [location.search]);

  // A language address decides the language. The switcher moves the address.
  useEffect(() => {
    if (brand.id !== 'catalogofworks' && brand.id !== 'sitonaim') return;
    if (!tenantSlugFromHostname()) return;
    const fromPath = /^\/(ru|en|he)(?:\/|$)/.exec(location.pathname)?.[1] as Language | undefined;
    if (!fromPath || fromPath === lang) return;
    setLangState(fromPath);
    persistPreferredLanguage(fromPath);
  }, [location.pathname, lang]);

  useEffect(() => {
    persistPreferredLanguage(lang);
  }, [lang]);

  useEffect(() => {
    if (tenant?.theme) {
      applyTenantThemeCss(normalizeTenantTheme(tenant.theme));
      return;
    }
    clearTenantThemeCss();
    applySystemTheme(config);
  }, [tenant?.theme, config]);

  useEffect(() => {
    if (loading) return;
    if (location.pathname !== '/') {
      clearBootSplashPaint();
    }
  }, [loading, location.pathname]);

  if (loading) {
    return (
      <div
        id="app-boot-loading"
        className="flex min-h-screen items-center justify-center text-[var(--text-primary)]"
      >
        {_t('common.loading', lang)}
      </div>
    );
  }

  const aliveApex = brand.id === 'alivealbum' && !tenantSlugFromHostname();
  const cowApex = brand.id === 'catalogofworks' && !tenantSlugFromHostname();
  const catalogTenant = (brand.id === 'catalogofworks' || brand.id === 'sitonaim')
    && Boolean(tenantSlugFromHostname());
  const catalogWorks = brand.id === 'catalogofworks' || brand.id === 'sitonaim';

  return (
    <ConfirmProvider lang={lang}>
    {/* Silent fallback: a chunk arrives in milliseconds, a flashing label would be noise. */}
    <Suspense fallback={null}>
    <Routes>
      <Route
        path="/"
        element={
          aliveApex ? (
            <AliveLandingV2
              lang={lang}
              pathLang={lang}
              setLang={setLang}
              loginTo="/login"
              appName={brand.displayName}
              page="home"
            />
          ) : isAliveAlbumShowcaseClosed(tenant) ? (
            <PublicShowcaseClosedPage lang={lang} />
          ) : (
            <Landing
              lang={lang}
              setLang={setLang}
              tenant={tenant}
              tenantSlug={tenant?.slug}
              tenantName={tenant?.name}
              landingI18n={tenant?.landingI18n}
              logoUrl={tenant?.logoUrl}
            />
          )
        }
      />
      {aliveApex
        ? (['ru', 'en', 'he'] as const).map((code) => (
          <Route
            key={code}
            path={`/${code}`}
            element={(
              <AliveLandingV2
                lang={lang}
                pathLang={code}
                setLang={setLang}
                loginTo="/login"
                appName={brand.displayName}
                page="home"
              />
            )}
          />
        ))
        : null}
      {cowApex
        ? (['ru', 'en', 'he'] as const).map((code) => (
          <Route
            key={`cow-${code}`}
            path={`/${code}`}
            element={(
              <Landing
                lang={lang}
                setLang={setLang}
                tenant={tenant}
                tenantSlug={tenant?.slug}
                tenantName={tenant?.name}
                landingI18n={tenant?.landingI18n}
                logoUrl={tenant?.logoUrl}
              />
            )}
          />
        ))
        : null}
      {catalogTenant
        ? (['ru', 'en', 'he'] as const).map((code) => (
          <Route
            key={`shop-${code}`}
            path={`/${code}`}
            element={(
              <Landing
                lang={lang}
                setLang={setLang}
                tenant={tenant}
                tenantSlug={tenant?.slug}
                tenantName={tenant?.name}
                landingI18n={tenant?.landingI18n}
                logoUrl={tenant?.logoUrl}
              />
            )}
          />
        ))
        : null}
      {catalogTenant
        ? ['/blog', '/blog/:postKey', ...(['ru', 'en', 'he'] as const).flatMap((code) => [`/${code}/blog`, `/${code}/blog/:postKey`])].map((path) => (
          <Route
            key={`blog-${path}`}
            path={path}
            element={(
              <Landing
                lang={lang}
                setLang={setLang}
                tenant={tenant}
                tenantSlug={tenant?.slug}
                tenantName={tenant?.name}
                landingI18n={tenant?.landingI18n}
                logoUrl={tenant?.logoUrl}
              />
            )}
          />
        ))
        : null}
      {aliveApex
        ? (['ru', 'en', 'he'] as const).flatMap((code) => (['travel', 'faq', 'books', 'showcase'] as const).map((page) => (
          <Route
            key={`${code}-${page}`}
            path={`/${code}/${page}`}
            element={(
              <AliveInfoPage
                lang={lang}
                setLang={setLang}
                loginTo="/login"
                appName={brand.displayName}
                page={page}
                pageLang={code}
              />
            )}
          />
        )))
        : null}
      {aliveApex
        ? (['ru', 'en', 'he'] as const).flatMap((pathLang) => (['pro', 'personal', 'pricing'] as const).map((page) => (
          <Route
            key={`v2-${pathLang}-${page}`}
            path={`/${pathLang}/${page}`}
            element={(
              <AliveLandingV2
                lang={lang}
                pathLang={pathLang}
                setLang={setLang}
                loginTo="/login"
                appName={brand.displayName}
                page={page}
              />
            )}
          />
        )))
        : null}
      {aliveApex
        ? (['ru', 'en', 'he'] as const).map((code) => (
          <Route key={`v2-${code}-preview`} path={`/${code}/preview`} element={<Navigate to={`/${code}`} replace />} />
        ))
        : null}
      <Route path="/login" element={<Login lang={lang} setLang={setLang} />} />
      <Route
        path="/legal/:page"
        element={<LegalPage lang={lang} setLang={setLang} tenant={tenant} />}
      />
      <Route
        path="/a"
        element={<AnnouncementsMobilePage lang={lang} setLang={setLang} />}
      />
      <Route
        path="/a/:code"
        element={<DigitalAlbumPage lang={lang} setLang={setLang} />}
      />
      <Route path="/b/:code" element={<PublicGlobalBookPage lang={lang} setLang={setLang} landingI18n={tenant?.landingI18n} logoUrl={tenant?.logoUrl} />} />
      <Route
        path="/g/:galleryId/d"
        element={<PublicGalleryRoute lang={lang} setLang={setLang} landingI18n={tenant?.landingI18n} logoUrl={tenant?.logoUrl} book />}
      />
      <Route
        path="/g/:galleryId/s/:code"
        element={(
          <PublicShowRoute lang={lang} setLang={setLang} landingI18n={tenant?.landingI18n} logoUrl={tenant?.logoUrl} />
        )}
      />
      <Route
        path="/g/:galleryId/s"
        element={(
          <PublicShowRoute lang={lang} setLang={setLang} landingI18n={tenant?.landingI18n} logoUrl={tenant?.logoUrl} />
        )}
      />
      <Route
        path="/g/:galleryId/bot"
        element={<AliveAlbumBotPage lang={lang} />}
      />
      {['d1', 'd2', 'd3', 'd4', 'd5', 'd6', 'd7', 'd8'].map((code) => (
        <Route
          key={code}
          path={`/g/:galleryId/${code}`}
          element={(
            <PublicGalleryRoute
              lang={lang}
              setLang={setLang}
              landingI18n={tenant?.landingI18n}
              logoUrl={tenant?.logoUrl}
              bookCode={code}
            />
          )}
        />
      ))}
      <Route
        path="/g/:galleryId"
        element={<PublicGalleryRoute lang={lang} setLang={setLang} landingI18n={tenant?.landingI18n} logoUrl={tenant?.logoUrl} />}
      />
      <Route path="/reviews" element={<PublicReviewsPage lang={lang} />} />
      {catalogWorks ? (
        <Route path="/w/:productId" element={<PublicWorkPage lang={lang} setLang={setLang} />} />
      ) : null}
      {catalogWorks ? (
        <Route path="/:workLang/w/:productId" element={<PublicWorkPage lang={lang} setLang={setLang} />} />
      ) : null}
      <Route
        path="/c"
        element={<VisitingCardPublicPage legalDocuments={tenant?.legalDocuments ?? null} />}
      />
      <Route path="/telegram/catalog" element={<TelegramMiniAppShell lang={lang} setLang={setLang} />} />
      <Route path="/telegram/cart" element={<TelegramMiniAppShell lang={lang} setLang={setLang} />} />
      <Route path="/telegram/favorites" element={<TelegramMiniAppShell lang={lang} setLang={setLang} />} />
      <Route path="/telegram/news" element={<TelegramMiniAppShell lang={lang} setLang={setLang} />} />
      <Route path="/telegram/about" element={<TelegramMiniAppShell lang={lang} setLang={setLang} />} />
      <Route path="/telegram/profile" element={<TelegramMiniAppShell lang={lang} setLang={setLang} />} />
      <Route path="/telegram/partner" element={<Navigate to="/partner/login" replace />} />
      <Route path="/partner/login" element={<PartnerLoginPage lang={lang} setLang={setLang} />} />
      <Route path="/partner" element={<PartnerCabinetPage lang={lang} setLang={setLang} />} />
      <Route path="/platform/login" element={<Navigate to="/login" replace />} />
      <Route path="/platform/admin" element={<PlatformAdminLayout lang={lang} setLang={setLang} />}>
        <Route index element={<PlatformOverviewPage lang={lang} />} />
        <Route path="tenants" element={<PlatformTenantsPage lang={lang} />} />
        <Route path="plans" element={<PlatformPlanCatalogPage lang={lang} />} />
        <Route path="gift-coupons" element={<PlatformGiftCouponsPage lang={lang} />} />
        <Route path="manual-payments" element={<PlatformManualPaymentsPage lang={lang} />} />
        <Route path="join-requests" element={<PlatformJoinRequestsPage lang={lang} />} />
        <Route path="referral-applications" element={<PlatformReferralApplicationsPage lang={lang} />} />
        <Route path="referral-partners" element={<PlatformReferralPartnersPage lang={lang} />} />
        <Route path="referral-conversions" element={<PlatformReferralConversionsPage lang={lang} />} />
        <Route path="referral-payouts" element={<PlatformReferralPayoutsPage lang={lang} />} />
        <Route path="referral-settings" element={<PlatformReferralSettingsPage lang={lang} />} />
        <Route path="extension-orders" element={<PlatformExtensionOrdersPage lang={lang} />} />
        <Route path="extensions" element={<PlatformExtensionsPage lang={lang} />} />
        <Route
          path="extensions/room-mockup-packs/:key"
          element={<PlatformRoomMockupPackPage lang={lang} />}
        />
        <Route path="ui-presets" element={<PlatformUiPresetsPage lang={lang} />} />
        <Route path="seo" element={<PlatformSeoPage lang={lang} />} />
        <Route element={<PlatformSuperAdminRoute />}>
          <Route path="staff" element={<PlatformStaffPage lang={lang} />} />
          <Route path="maintenance" element={<PlatformMaintenancePage lang={lang} />} />
        </Route>
        <Route path="profile" element={<PlatformProfilePage lang={lang} />} />
        <Route path="system" element={<Navigate to="/platform/admin/maintenance" replace />} />
      </Route>

      <Route
        path="/app"
        element={
          missingTenantOnApp ? (
            <Navigate to="/" replace />
          ) : error && !tenant ? (
            <div className="flex min-h-screen flex-col items-center justify-center gap-2 px-4 text-center">
              <p className="text-red-600">{error}</p>
              <p className="text-sm text-[var(--text-secondary)]">
                {_t('app.noWarehouseHint', lang)}
              </p>
            </div>
          ) : (
            <StorefrontFaceGate tenant={tenant} lang={lang}>
              <AppLayout lang={lang} setLang={setLang} tenant={tenant} />
            </StorefrontFaceGate>
          )
        }
      >
        <Route index element={<AppIndexRedirect />} />
        <Route path="catalog" element={<CatalogView />} />
        <Route path="favorites" element={<FavoritesView />} />
        {isBrandStorefrontPathEnabled('cart') ? (
          <Route path="cart" element={<CartView />} />
        ) : null}
        <Route path="news" element={<NewsView />} />
        <Route path="profile" element={<ProfileView />} />
        <Route path="about" element={<AboutView />} />
        <Route
          path="overview"
          element={
            <TenantModuleGuard module="overview">
              <OverviewView />
            </TenantModuleGuard>
          }
        />
        {brand.id === 'alivealbum' ? (
          <>
            <Route path="plan" element={<PhotographerPlanView />} />
            <Route path="trash" element={<TrashView />} />
          </>
        ) : null}
        {brand.id === 'alivealbum' ? (
          <>
            <Route path="links" element={<Navigate to=".." replace />} />
            <Route path="account" element={<Navigate to=".." replace />} />
            <Route path="upload" element={<Navigate to=".." replace />} />
          </>
        ) : null}
        {isBrandAdminModuleEnabled('announcements') ? (
          <Route
            path="announcements"
            element={
              <TenantModuleGuard module="announcements">
                <AnnouncementsView />
              </TenantModuleGuard>
            }
          />
        ) : null}
        {isBrandAdminModuleEnabled('notes') ? (
          <Route
            path="notes"
            element={
              <TenantModuleGuard module="notes">
                <NotesView />
              </TenantModuleGuard>
            }
          />
        ) : null}
        {isBrandAdminModuleEnabled('stock_alerts') ? (
          <Route
            path="stock-alerts"
            element={
              <TenantModuleGuard module="stock_alerts">
                <StockAlertsView />
              </TenantModuleGuard>
            }
          />
        ) : null}
        <Route
          path="crm"
          element={
            <TenantModuleGuard module="crm">
              <CrmView />
            </TenantModuleGuard>
          }
        />
        {isBrandAdminModuleEnabled('album_reviews') ? (
          <Route
            path="reviews"
            element={
              <TenantModuleGuard module="album_reviews">
                <AlbumReviewsView />
              </TenantModuleGuard>
            }
          />
        ) : null}
        {isBrandAdminModuleEnabled('album_published_reviews') ? (
          <Route
            path="published-reviews"
            element={
              <TenantModuleGuard module="album_published_reviews">
                <AlbumPublishedReviewsView />
              </TenantModuleGuard>
            }
          />
        ) : null}
        {isBrandAdminModuleEnabled('album_requests') ? (
          <Route
            path="requests"
            element={
              <TenantModuleGuard module="album_requests">
                <AlbumRequestsView />
              </TenantModuleGuard>
            }
          />
        ) : null}
        {isBrandAdminModuleEnabled('album_books') ? (
          <Route
            path="books"
            element={
              <TenantModuleGuard module="album_books">
                <BooksView />
              </TenantModuleGuard>
            }
          />
        ) : null}
        {isBrandAdminModuleEnabled('album_books')
          ? ['books/new', 'books/global/:bookId', 'books/:galleryId/:code'].map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <TenantModuleGuard module="album_books">
                  <BookEditorView />
                </TenantModuleGuard>
              }
            />
          ))
          : null}
        {isBrandAdminModuleEnabled('galleries') ? (
          <Route
            path="galleries"
            element={
              <TenantModuleGuard module="galleries">
                <GalleriesView />
              </TenantModuleGuard>
            }
          />
        ) : null}
        {isBrandAdminModuleEnabled('galleries') ? (
          <Route
            path="galleries/new"
            element={
              <TenantModuleGuard module="galleries">
                <GallerySettingsView />
              </TenantModuleGuard>
            }
          />
        ) : null}
        {isBrandAdminModuleEnabled('galleries') ? (
          <Route
            path="galleries/:galleryId"
            element={
              <TenantModuleGuard module="galleries">
                <GallerySettingsView />
              </TenantModuleGuard>
            }
          />
        ) : null}
        {isBrandAdminModuleEnabled('products') ? (
          <Route
            path="products"
            element={
              <TenantModuleGuard module="products">
                <ProductsView />
              </TenantModuleGuard>
            }
          />
        ) : null}
        {isBrandAdminModuleEnabled('prices') ? (
          <Route
            path="prices"
            element={
              <TenantModuleGuard module="prices">
                <PricesView />
              </TenantModuleGuard>
            }
          />
        ) : null}
        {isBrandAdminModuleEnabled('promotions') ? (
          <Route
            path="promotions"
            element={
              <TenantModuleGuard module="promotions">
                <PromotionsView />
              </TenantModuleGuard>
            }
          />
        ) : null}
        {isBrandAdminModuleEnabled('stock') ? (
          <Route
            path="stock"
            element={
              <TenantModuleGuard module="stock">
                <StockView />
              </TenantModuleGuard>
            }
          />
        ) : null}
        {isBrandAdminModuleEnabled('categories') ? (
          <Route
            path="categories"
            element={
              <TenantModuleGuard module="categories">
                <CategoriesView />
              </TenantModuleGuard>
            }
          />
        ) : null}
        <Route
          path="attributes"
          element={
            <TenantModuleGuard module="attributes">
              <AttributesView />
            </TenantModuleGuard>
          }
        />
        <Route
          path="option-sets"
          element={
            <TenantModuleGuard module="optionSets">
              <OptionSetsView />
            </TenantModuleGuard>
          }
        />
        <Route
          path="variant-templates"
          element={
            <TenantModuleGuard module="variant_templates">
              <VariantTemplatesView />
            </TenantModuleGuard>
          }
        />
        <Route
          path="addon-templates"
          element={
            <TenantModuleGuard module="addon_templates">
              <AddonTemplatesView />
            </TenantModuleGuard>
          }
        />
        <Route
          path="subcategories"
          element={
            <TenantModuleGuard module="subcategories">
              <SubcategoriesView />
            </TenantModuleGuard>
          }
        />
        {isBrandAdminModuleEnabled('import') ? (
          <Route
            path="import"
            element={
              <TenantModuleGuard module="import">
                <ImportView />
              </TenantModuleGuard>
            }
          />
        ) : null}
        {isBrandAdminModuleEnabled('import_media') ? (
          <Route
            path="import-media"
            element={
              <TenantModuleGuard module="import_media">
                <ImportMediaView />
              </TenantModuleGuard>
            }
          />
        ) : null}
        <Route
          path="export"
          element={
            <TenantModuleGuard module="export">
              <ExportView />
            </TenantModuleGuard>
          }
        />
        <Route
          path="albums"
          element={
            <TenantModuleGuard module="albums">
              <AlbumsView />
            </TenantModuleGuard>
          }
        />
        <Route
          path="visiting-card"
          element={
            <TenantModuleGuard module="visiting_card">
              <VisitingCardView />
            </TenantModuleGuard>
          }
        />
        {isBrandAdminModuleEnabled('marketplace') ? (
          <Route
            path="marketplace"
            element={
              <TenantModuleGuard module="marketplace">
                <MarketplaceView />
              </TenantModuleGuard>
            }
          />
        ) : null}
        <Route
          path="publisher"
          element={
            <TenantModuleGuard module="publisher">
              <PublisherView />
            </TenantModuleGuard>
          }
        />
        {isBrandAdminModuleEnabled('orders') ? (
          <Route
            path="orders"
            element={
              <TenantModuleGuard module="orders">
                <OrdersView />
              </TenantModuleGuard>
            }
          />
        ) : null}
        {isBrandAdminModuleEnabled('staff') ? (
          <Route
            path="staff"
            element={
              <TenantModuleGuard module="staff">
                <StaffView />
              </TenantModuleGuard>
            }
          />
        ) : null}
        <Route
          path="settings"
          element={
            <TenantModuleGuard module="settings">
              <SettingsView />
            </TenantModuleGuard>
          }
        />
      </Route>

      <Route path="*" element={<MissingPage lang={lang} />} />
    </Routes>
    </Suspense>
    {tenant
      && !isPlatformRoute
      && !location.pathname.startsWith('/telegram')
      && !location.pathname.startsWith('/c')
      ? (
      <CookieConsentBanner
        lang={extraShopLanguages && publicShopChrome ? shopChromeLang : lang}
        tenantSlug={tenant.slug}
        legalDocuments={tenant.legalDocuments}
      />
    ) : null}
    </ConfirmProvider>
  );
}

/** Repaint when a language chunk arrives, so switching does not leave labels empty. */
function useI18nRepaint() {
  const [, bump] = useState(0);
  useEffect(() => {
    const onLoaded = () => bump((v) => v + 1);
    window.addEventListener(I18N_LOADED_EVENT, onLoaded);
    return () => window.removeEventListener(I18N_LOADED_EVENT, onLoaded);
  }, []);
}

export default function App() {
  useI18nRepaint();
  return (
    <AuthProvider>
      <SystemConfigProvider>
        <BrowserRouter>
          <FloatingFabDockProvider>
            <AppRoutes />
          </FloatingFabDockProvider>
        </BrowserRouter>
      </SystemConfigProvider>
    </AuthProvider>
  );
}
